/**
 * CanonSyncCard (iter86.82)
 *
 * The dev → main PROMOTE merge is no longer part of the per-iter
 * runbook (pr_approved gate retired). It's a BATCHED ritual on its own
 * (~once a day): open ONE PR covering every iter since the last
 * sync, let CI re-verify the batched state, review the combined diff
 * on GitHub, merge. Beta is unaffected — Emergent deploys (gated by
 * full CI on each dev push) already shipped the code; main is the
 * canon/audit copy.
 *
 * This card keeps the batching honest: "dev is N commits ahead of main
 * — last sync X days ago" + a one-click compare/PR link, and embeds the
 * existing PROMOTE PR tracker (CI status on the PR head).
 */
import { useEffect, useState } from "react";
import { GitMerge, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { OpenPullRequestsCardContent } from "./CIStatusCards";

export function CanonSyncCard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.githubCanonSync().then((r) => { if (!cancelled) setData(r); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const ahead = data?.ahead_by;
    const daysAgo = data?.last_sync_at
        ? Math.floor((Date.now() - new Date(data.last_sync_at).getTime()) / 86400000)
        : null;

    return (
        <CollapsibleCard
            testId="canon-sync-card"
            icon={GitMerge}
            title="Sync to Main — dev → main (batched)"
            badge="own cadence"
            badgeClass="bg-sky-500/15 text-sky-300 border-sky-500/40"
        >
            <p className="text-[11px] text-zinc-400 leading-relaxed mb-2" data-testid="canon-sync-explainer">
                Not part of the per-iter runbook. Batch a day's iters into ONE
                PROMOTE PR — CI re-verifies the batch, you review the combined
                diff, merge. Beta is unaffected (already deployed via Emergent);{" "}
                <span className="font-mono">main</span> is the canon copy.
            </p>

            {!data ? (
                <p className="text-xs text-zinc-500 italic">Loading…</p>
            ) : !data.configured ? (
                <p className="text-xs text-zinc-500 italic" data-testid="canon-sync-unconfigured">
                    GitHub not configured (GITHUB_TOKEN / GITHUB_REPO) or checks branch is main.
                </p>
            ) : (
                <div className="flex items-center flex-wrap gap-2 mb-2" data-testid="canon-sync-status-row">
                    <span className={`pill text-[10px] py-0.5 px-2 border ${
                        ahead > 0 ? "border-sky-500/40 bg-sky-500/15 text-sky-300" : "border-emerald-500/40 bg-emerald-500/15 text-emerald-400"
                    }`} data-testid="canon-sync-ahead-pill">
                        {ahead === 0 ? "dev = main — in sync" : `dev is ${ahead} commit${ahead === 1 ? "" : "s"} ahead of main`}
                    </span>
                    {daysAgo !== null && (
                        <span className="text-[10px] text-zinc-500" data-testid="canon-sync-last-sync">
                            last sync {daysAgo === 0 ? "today" : `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`}
                            {data.last_sync_pr ? ` (PR #${data.last_sync_pr.number})` : ""}
                        </span>
                    )}
                    {ahead > 0 && !data.open_pr && data.compare_url && (
                        <a
                            href={data.compare_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-volt underline hover:no-underline"
                            data-testid="canon-sync-open-pr-link"
                        >
                            Open PROMOTE PR <ExternalLink className="w-3 h-3" />
                        </a>
                    )}
                </div>
            )}

            <OpenPullRequestsCardContent />
        </CollapsibleCard>
    );
}
